import { t } from "@/lib/i18n"

/**
 * Shape, endpoints and copy lookup for saying why something was cancelled.
 *
 * **The browser invents no reason.** `Location::CancellationPolicy` is the
 * single catalog: a bill cancelled from the table workspace and an order
 * cancelled from the board or its drawer both draw from the same six
 * SELECTABLE_REASONS, sent once per page and never re-derived here. The
 * seventh value, `bill_closed_pending`, is excluded before it ever reaches the
 * browser — it is the system's own sweep reason, not something a human picks.
 *
 * **The reason is mandatory, not a courtesy field.** `Location::CancellationPolicy.label`
 * resolves through `Localization::Text.enum`, which calls `I18n.t!` and raises
 * on a key it does not recognise rather than falling back to one. A
 * cancellation with no reason used to 500 the till screen for exactly that
 * reason: the server has nowhere safe to fall back to, so this dialog is the
 * one place in the browser that is allowed to produce one, and it only ever
 * offers what the server sent.
 *
 * ## One dialog, two endpoints
 *
 * A bill and an order line are cancelled by different requests —
 * `PATCH /bills/:id/cancel` reads `cancel_reason` at the top level;
 * `PATCH /orders/:id` reads it nested under `order`, and only takes effect
 * when `status` is also `cancelled`. Both are named here so the dialog itself
 * stays ignorant of which resource it is cancelling.
 */

export type CancellationReasonOption = {
  value: string
  label: string
}

export const CANCEL_PATHS = {
  bill: (billId: number) => `/bills/${billId}/cancel`,
  order: (orderId: number) => `/orders/${orderId}`,
} as const

/**
 * What `patchInline` accepts, spelled out locally rather than pulled from
 * `@inertiajs/core`: the same call `inline-update.ts` already makes to keep
 * that package out of this feature's direct dependencies for the sake of one
 * type.
 */
export type CancelPayload = Record<string, string | Record<string, string>>

/** The location feature-independent catalog is always sent; nothing gates it. */
const I18N_PREFIX = "ui.react.features.staff.cancellations"

export function tCancellation(key: string, options: Record<string, string | number> = {}) {
  return t(`${I18N_PREFIX}.${key}`, options)
}

/** The body `Staff::Location::BillsController#cancel` reads. */
export function billCancelPayload(reason: string): CancelPayload {
  return { cancel_reason: reason }
}

/**
 * The body `Staff::Location::OrdersController#update` reads.
 *
 * `cancellation_aware_attributes` only stamps a reason when `status` is also
 * `cancelled` in the same request — sending the reason alone would be dropped
 * silently, which is worse than refusing it, so both travel together.
 */
export function orderCancelPayload(reason: string): CancelPayload {
  return { order: { status: "cancelled", cancel_reason: reason } }
}

/** Whether the confirm button has the one thing the endpoint needs. */
export function cancellationRequestComplete(reason: string): boolean {
  return reason.length > 0
}
